import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Tooltip,
} from '@mui/material';
import { isEmpty } from 'lodash';
import Swal from 'sweetalert2';

import clientGraphql from '../../common/apollo-graphql';
import AuthContext from '../auth/context/AuthContext';
import { MyButton, MyTypography } from '../common/components';
import Error from '../common/Error';
import StringUtils from '../common/services/StringUtils';
import FileUtils from '../common/services/FileUtils';
import HelperUtils from '../common/services/HelperUtils';

import { GET_REFERRAL_BY_UUID, REFERRAL_ANSWERS_QUERY } from './gql';
import { REFERRAL_PATIENT_DETAIL } from './gql/Referral/Query';
import Referral from './models/Referral';

import { useGetReferral } from './redux/getReferral';
import { useSendReferral } from './redux/sendReferral';
import { useFaxSend } from './redux/faxSend';

import PatientDetails from './patient/PatientDetails';
import UploadedDocuments from '../patient/UploadedDocuments';
import AllReferralNotes from './notes/AllReferralNotes';
import { useLocation } from 'react-router-dom/cjs/react-router-dom';
import BackToDashboard from '../common/components/BackToDashboard';
import GlobalLoader from '../common/components/loader/GlobalLoader';
import { MANUALLY_CREATED_STATUS } from './constants/referral.constants';

// -----------------------------------------------------------------------------
// MAIN COMPONENT
// -----------------------------------------------------------------------------
export default function ReferralDetails({
  referralId,
  referralStatus = null,
  refresh = false,
  setRefresh,
  onDisableSendReferral = null,
  onSetParsedData = null,
  setCreateFileObjects = null,
  referralDetails = null,
  setReferralDetails = null,
  patientDetails = null,
  setPatientDetails = null,
  isReferrer = false,
  setIsFaxSending = null,
  handleOpenAppointment = null,
  handleOpenUploadDocuments = null,
  AddScheduleComponent = null,
  AcceptButtonComponent = null,
  DeclineButtonComponent = null,
  EndorseButtonComponent = null,
  UnderReviewButtonComponent = null,
  UpdateStatusButtonComponent = null,
  ResendFaxButtonComponent = null,
}) {
  const location = useLocation();
  const isReviewPage = location.pathname.includes("review");
  // ---------------------------------------------------------------------------
  // CONTEXT & STATE
  // ---------------------------------------------------------------------------
  const currentUser = useContext(AuthContext);
  const { faxSend } = useFaxSend();
  const { getReferral } = useGetReferral();
  const { sendReferralError } = useSendReferral();

  const [parsedData, setParsedData] = useState({});
  const [uploadedFileObjects, setUploadedFileObjects] = useState({});


  // ---------------------------------------------------------------------------
  // QUERIES
  // ---------------------------------------------------------------------------
  const { data, refetch } = useQuery(GET_REFERRAL_BY_UUID, {
    variables: { uuid: referralId },
    skip: !referralId,
    client: clientGraphql,
    refetchOnWindowFocus: false,
  });

  const { data: patientFullDetail } = useQuery(REFERRAL_PATIENT_DETAIL, {
    variables: { uuid: referralId },
    client: clientGraphql,
    refetchOnWindowFocus: false,
  });

  const { data: data_referral_answers } = useQuery(REFERRAL_ANSWERS_QUERY, {
    variables: { uuid: referralId },
    client: clientGraphql,
    refetchOnWindowFocus: false,
  });


  // ---------------------------------------------------------------------------
  // EFFECTS - REFERRAL DETAILS
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (refresh) {
      refetch();
      setRefresh(false); // reset the flag
    }
  }, [refresh, refetch, setRefresh]);
  useEffect(() => {
    if (!isEmpty(data)) {
      const { referral } = data;
      const referralWithUser = { ...referral, user_uuid: currentUser.uuid };

      // Set referral details if parent provided setter
      if (setReferralDetails && isEmpty(referralDetails)) {
        setReferralDetails(data);
      }

      setParsedData({
        ...referralWithUser,
        referral: new Referral(referralWithUser),
      });
    } else {
      setParsedData({});
    }
  }, [data, currentUser, setReferralDetails, referralDetails]);

  // Sync parsedData to parent
  useEffect(() => {
    if (onSetParsedData && !isEmpty(parsedData)) {
      onSetParsedData(parsedData);
    }
  }, [parsedData, onSetParsedData]);

  // Set patient details when query returns
  useEffect(() => {
    if (!isEmpty(patientFullDetail) && setPatientDetails && isEmpty(patientDetails)) {
      setPatientDetails(patientFullDetail);
    }
  }, [patientFullDetail, setPatientDetails, patientDetails]);


  // ---------------------------------------------------------------------------
  // EFFECTS - DOCUMENTS
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (isEmpty(referralId) && !refresh) return;

    getReferral(referralId).then(response => {
      const documents = response.data.documents;
      const questions = [];

      Object.keys(documents).forEach(key => {
        documents[key].forEach((uploadedDoc, index) => {
          const q = uploadedDoc.question?.[0]?.question;

          if (!isEmpty(q) && q !== '?' && questions.includes(q)) {
            documents[key][index].question = [];
          } else if (
            !isEmpty(q) &&
            !isEmpty(data_referral_answers?.answers) &&
            data_referral_answers.answers.some(ref => ref.question.toLowerCase() === q.toLowerCase()) &&
            q !== '?'
          ) {
            questions.push(q);
          }
        });
      });

      setUploadedFileObjects(documents);
      setCreateFileObjects(documents);
    });
  }, [referralId, refresh, getReferral, data_referral_answers, setCreateFileObjects]);


  // ---------------------------------------------------------------------------
  // EFFECTS - DISABLE SEND REFERRAL
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (isEmpty(parsedData) || isEmpty(uploadedFileObjects) || !onDisableSendReferral) return;

    let isDisabled = false;
    const { referral } = parsedData;

    referral.referral.provider.documents.forEach(doc => {
      if (doc.required && !(doc.document.name in uploadedFileObjects)) {
        isDisabled = true;
      }
    });

    onDisableSendReferral(isDisabled);
  }, [referralId, uploadedFileObjects, onDisableSendReferral, parsedData]);


  // ---------------------------------------------------------------------------
  // FAX HANDLING
  // ---------------------------------------------------------------------------
  const sendFax = useCallback((uploadedDoc, key) => {
    const uploadedDocuments = { [key]: [uploadedDoc] };
    FileUtils.faxSend(patientDetails, uploadedDocuments, parsedData, currentUser)
      .then(res => {
        const faxNumber = HelperUtils.getFaxNumber(parsedData);
        const formData = new FormData();

        formData.append('file', res);
        formData.append('recipient', '001' + faxNumber.replace('-', ''));
        formData.append('referral_uuid', referralId);

        faxSend(formData)
          .then(resultData => {
            Swal.fire({ icon: 'success', text: resultData.message, showConfirmButton: true })
              .then(() => setIsFaxSending(false));
          })
          .catch(errorData => {
            Swal.fire({
              icon: 'error',
              text: errorData.data?.error || 'Fax sending failed.',
              showConfirmButton: true,
            }).then(() => setIsFaxSending(false));
          });
      })
      .catch(errorData => {
        Swal.fire({
          icon: 'error',
          text: errorData || 'Error generating file for fax.',
          showConfirmButton: true,
        }).then(() => setIsFaxSending(false));
      });
  }, [setIsFaxSending, faxSend, currentUser, patientDetails, parsedData, referralId]);

  const handleSendFax = useCallback((uploadedDoc, key) => {
    sendFax(uploadedDoc, key);
  }, [sendFax]);

  const statusLabel = !isEmpty(referralStatus)
    ? StringUtils.toTitleCase2(referralStatus)
    : parsedData?.referral?.getReferralStatus(currentUser) || '';

  const isDraft = parsedData?.status === 'draft';

  const UploadButtonComponent = () => {
    const status = parsedData?.status;

    if (!status) return null;

    const renderButton = () => (
      <MyButton
        type="button"
        color="primary"
        variant="outlined"
        onClick={handleOpenUploadDocuments}
        fullWidth={false}
      >
        Upload Documents
      </MyButton>
    );

    // Allow upload if manually created and has clinic receiver
    if (status === MANUALLY_CREATED_STATUS) {
      if (parsedData?.provider) {
        return renderButton();
      } else {
        return <></>; // TODO: add something here that explains why we can't upload since we have no clinic receiver
      }
    }

    // Original allowed statuses
    const allowedStatuses = ["under_review", "sent", "accepted", "scheduled"];

    if (isReferrer && allowedStatuses.includes(status)) {
      return renderButton();
    }

    return null;
  };

  const ActionAndStatusComponent = () => (
    <>
      <Grid item xs={12} sx={{mx: 2}}>
        <Stack direction="row" spacing={1} alignItems="center">
          <MyTypography variant="h4">Status:</MyTypography>
          <Tooltip title={'Status'} arrow>
            <Chip
              label={statusLabel}
              sx={{
                color: 'rgb(22 101 52/var(--tw-text-opacity,1))',
                fontWeight: 'bold',
              }}
            />
          </Tooltip>

          {UpdateStatusButtonComponent}

          <Box sx={{ flexGrow: 1 }} />

          {EndorseButtonComponent}
          {UnderReviewButtonComponent}
          {ResendFaxButtonComponent}

          <Box sx={{ flexGrow: 1 }} />

          {AddScheduleComponent}
          {AcceptButtonComponent}
          {DeclineButtonComponent}

          {parsedData?.referral?.referral?.appointment && (
            <>
              <MyTypography variant="h4">Appointment Date:</MyTypography>
              <Tooltip title={'Appointment Date'} arrow>
                <Chip
                  label={parsedData.referral.getDateOrReason()}
                  color="primary"
                  onClick={e => {
                    e.preventDefault();
                    handleOpenAppointment();
                  }}
                  clickable={true}
                />
              </Tooltip>
            </>
          )}

          {parsedData?.referral?.referral?.reason && (
            <>
              <MyTypography variant="h4">Decline Reason:</MyTypography>
              <Tooltip title={'Decline Reason'} arrow>
                <Chip
                  label={parsedData.referral.getDateOrReason()}
                  color="secondary"
                />
              </Tooltip>
            </>
          )}
        </Stack>
      </Grid>
    </>
  );

  const DataComponent = () => (
    <>
      <ActionAndStatusComponent />

      <Divider sx={{ my: 2 }} />

      <Stack direction="column" spacing={3}>
        {/* Patient Details */}
        <PatientDetails
          referralId={referralId}
          isDraft={isDraft}
          parsedData={parsedData}
          referralRefetch={refetch}
        />

        {/* Uploaded Documents */}
        {!isEmpty(patientFullDetail) && !isEmpty(data) && (
          <Paper
            sx={{
              borderRadius: 3,
              border: 1,
              borderColor: theme => theme.palette.divider,
              p: 3,
              backgroundColor: 'background.paper',
              width: '100%',
            }}
          >
            <UploadedDocuments
              isReferrer={isReferrer}
              referralDetails={data}
              patientDetails={patientFullDetail}
              data_referral_answers={data_referral_answers}
              uploadedFileObjects={uploadedFileObjects}
              currentUser={currentUser}
              sendFax={parsedData?.is_fax ? handleSendFax : null}
              UploadButtonComponent={<UploadButtonComponent />}
            />
          </Paper>
        )}
      </Stack>
    </>
  );

  if (isEmpty(parsedData)) {
    return <GlobalLoader />;
  }

  return (
    <Card>
      <CardContent>
        <Grid container direction="column">
          {sendReferralError && (
            <Grid item xs={12} sx={{ pb: 2 }}>
              <Error error={sendReferralError} />
            </Grid>
          )}

          {isReviewPage ? (
            <>
              <BackToDashboard />

              <AllReferralNotes
                uuid={referralId}
                isExternal
                isDataTab
                DataComponent={<DataComponent />}
              />
            </>
          ) : (
            <DataComponent />
          )}
        </Grid>
      </CardContent>
    </Card>
  );
}
